import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, ShoppingBag, Check, X, Info } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ALLERGENS } from "@/lib/allergens";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, t } = useApp();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [removedIngredients, setRemovedIngredients] = useState([]);
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  // group_id -> { optionId, name, price, groupName }
  const [selectedOptions, setSelectedOptions] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/menu/products/${id}`);
        setProduct(res.data);
        // Pre-select the first choice of each option group so checkout is never blocked.
        const defaults = {};
        (res.data.option_groups || []).forEach(g => {
          const first = (g.options || [])[0];
          if (first) defaults[g.id] = { optionId: first.id, name: first.name, price: first.price || 0, groupName: g.name, allergens: first.allergens || [] };
        });
        setSelectedOptions(defaults);
      } catch (e) {
        toast.error(t("Product not found"));
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const chooseOption = (group, option) => {
    setSelectedOptions(prev => ({
      ...prev,
      [group.id]: { optionId: option.id, name: option.name, price: option.price || 0, groupName: group.name, allergens: option.allergens || [] },
    }));
  };

  const toggleIngredient = (ingId) => {
    setRemovedIngredients(prev =>
      prev.includes(ingId) ? prev.filter(i => i !== ingId) : [...prev, ingId]
    );
  };

  const toggleModifier = (mod) => {
    setSelectedModifiers(prev => {
      const exists = prev.find(m => m.modifierId === mod.id);
      if (exists) return prev.filter(m => m.modifierId !== mod.id);
      return [...prev, { modifierId: mod.id, name: mod.name, price: mod.price, quantity: 1, allergens: mod.allergens || [] }];
    });
  };

  const updateModifierQty = (modId, delta) => {
    const mod = product.modifiers.find(m => m.id === modId);
    setSelectedModifiers(prev => prev.map(m => {
      if (m.modifierId !== modId) return m;
      const newQty = Math.max(1, Math.min(m.quantity + delta, mod?.max_quantity || 3));
      return { ...m, quantity: newQty };
    }));
  };

  const estimatedPrice = () => {
    if (!product) return 0;
    let price = product.base_price;
    selectedModifiers.forEach(mod => { price += mod.price * mod.quantity; });
    Object.values(selectedOptions).forEach(o => { price += o.price || 0; });
    return price * quantity;
  };

  const handleAddToCart = () => {
    const selOpts = Object.entries(selectedOptions).map(([groupId, o]) => ({
      groupId, optionId: o.optionId, name: o.name, price: o.price || 0, groupName: o.groupName,
    }));
    addToCart({ product, quantity, removedIngredients, modifiers: selectedModifiers, selectedOptions: selOpts });
    toast.success(`${product.name} ${t("added to cart")}`);
  };

  if (loading) {
    return (
      <div className="relative z-[1] -mt-16 md:-mt-20">
        <Skeleton className="h-[44vh] min-h-[320px] w-full rounded-none" />
        <div className="p-6 max-w-2xl mx-auto space-y-4">
          <Skeleton className="h-9 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      </div>
    );
  }

  if (!product) return null;
  const removableIngredients = product.ingredients?.filter(i => i.removable) || [];
  const isOutOfStock = !!product.out_of_stock;
  const lang = (typeof localStorage !== "undefined" && localStorage.getItem("biten_lang")) === "en" ? "en" : "sv";
  // Allergens reflect the base product + whatever the customer has selected
  // (option choices + add-ons), updating live as they change their picks.
  const activeAllergenKeys = new Set(product.allergens || []);
  Object.values(selectedOptions).forEach(o => (o.allergens || []).forEach(k => activeAllergenKeys.add(k)));
  selectedModifiers.forEach(m => (m.allergens || []).forEach(k => activeAllergenKeys.add(k)));
  const productAllergens = ALLERGENS.filter(a => activeAllergenKeys.has(a.key));

  return (
    <div data-testid="product-detail-page" className="pb-32 relative z-[1]">
      {/* ===== Hero — full-bleed under the navbar, title set into the image ===== */}
      <div className="keep-dark relative h-[46vh] min-h-[340px] overflow-hidden -mt-16 md:-mt-20">
        {product.image_url ? (
          <motion.img
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#16130D] to-[#0E0E0F] flex items-center justify-center">
            <span className="font-playfair italic text-[#D4B483]/25 text-7xl select-none">BB</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/45 to-[#0A0A0A]/55" />
        <div className="absolute inset-0 noise-overlay" />

        <button
          onClick={() => navigate(-1)}
          className="absolute top-20 md:top-24 left-4 md:left-8 w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors z-10"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-[#EDEDED]" />
        </button>

        <div className="absolute bottom-0 left-0 right-0 px-5 md:px-8 pb-7 md:pb-9">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl mx-auto"
          >
            <span className="lux-eyebrow">{t("Bästa Biten")}</span>
            <div className="flex items-end justify-between gap-4 mt-3">
              <h1 className="font-playfair text-3xl md:text-5xl text-[#F5F1E8] leading-tight lux-title">
                {product.name}
              </h1>
              <p className="font-mono-price text-2xl md:text-3xl text-gold whitespace-nowrap pb-0.5">{product.base_price.toFixed(0)} kr</p>
            </div>
            {product.description && (
              <p className="text-[#B8B8BE] mt-3 text-sm md:text-base leading-relaxed font-light max-w-xl">{product.description}</p>
            )}
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="px-4 md:px-8 relative z-10 max-w-2xl mx-auto pt-6"
      >
        {isOutOfStock && (
          <div className="mb-5 bg-[#DC2626]/10 border border-[#DC2626]/40 text-[#FCA5A5] text-sm font-semibold rounded-xl px-4 py-3">
            {t("Out of stock")} – {t("This product cannot be ordered right now.")}
          </div>
        )}

        {/* Option groups — pick exactly one per group */}
        {(product.option_groups || []).map((group, gi) => (
          <motion.div
            key={group.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + gi * 0.05 }}
            className="lux-surface p-6 mb-5"
          >
            <h3 className="font-playfair text-xl text-[#F5F1E8] mb-1">{group.name}</h3>
            <p className="text-[#A8ABB2] text-xs italic mb-4">{t("Choose one")}</p>
            <div className="space-y-2.5">
              {(group.options || []).map((option) => {
                const chosen = selectedOptions[group.id]?.optionId === option.id;
                return (
                  <button
                    type="button"
                    key={option.id}
                    onClick={() => chooseOption(group, option)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all duration-300 ${
                      chosen
                        ? "border-[#D4B483] bg-[#D4B483]/8 modifier-glow"
                        : "border-[#27272A] bg-[#0A0A0A] hover:border-[#3F3F46] hover:bg-[#121212]"
                    }`}
                    data-testid={`option-${option.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${chosen ? "border-[#D4B483]" : "border-[#3F3F46]"}`}>
                        {chosen && <span className="w-2.5 h-2.5 rounded-full bg-[#D4B483]" />}
                      </span>
                      <span className={`text-sm font-medium ${chosen ? "text-[#EDEDED]" : "text-[#D4D4D8]"}`}>{option.name}</span>
                    </div>
                    {option.price > 0 && (
                      <span className={`font-mono-price text-sm font-bold ${chosen ? "text-[#D4B483]" : "text-[#A8ABB2]"}`}>+{option.price.toFixed(0)} kr</span>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        ))}

        {/* Allergens — EU declaration requirement */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="lux-surface p-6 mb-5"
        >
          <h3 className="font-playfair text-xl text-[#F5F1E8] mb-1">{t("Allergens")}</h3>
          {productAllergens.length > 0 ? (
            <>
              <p className="text-[#A8ABB2] text-xs italic mb-4">{t("Contains the following allergens")}</p>
              <div className="flex flex-wrap gap-2">
                {productAllergens.map((a) => (
                  <span key={a.key} className="inline-flex items-center gap-1.5 bg-[#D4B483]/8 border border-[#D4B483]/25 text-[#D4B483] text-sm px-3 py-1.5 rounded-full">
                    <span aria-hidden="true">{a.emoji}</span>
                    {a[lang]}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <p className="text-[#D4D4D8] text-sm flex items-start gap-2 mt-1">
              <Info className="w-4 h-4 text-[#D4B483] mt-0.5 flex-shrink-0" />
              {t("No allergens listed. Ask our staff about allergens before ordering.")}
            </p>
          )}
        </motion.div>

        {/* Ingredients - Creative Pill Controls */}
        {removableIngredients.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="lux-surface p-6"
          >
            <h3 className="font-playfair text-xl text-[#F5F1E8] mb-1">{t("Ingredients")}</h3>
            <p className="text-[#A8ABB2] text-xs italic mb-4">{t("Tap to remove what you don't want")}</p>
            <div className="flex flex-wrap gap-2">
              {removableIngredients.map((ing, i) => {
                const removed = removedIngredients.includes(ing.id);
                return (
                  <motion.button
                    key={ing.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.35 + i * 0.05 }}
                    whileTap={{ scale: 0.93 }}
                    onClick={() => toggleIngredient(ing.id)}
                    className={`relative flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                      removed
                        ? "bg-[#0A0A0A] border border-[#1A1A1A] text-[#3F3F46]"
                        : "bg-[#D4B483]/10 border border-[#D4B483]/30 text-[#D4B483] ingredient-active"
                    }`}
                    data-testid={`ingredient-toggle-${ing.id}`}
                  >
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      removed ? "bg-[#27272A]" : "bg-[#D4B483]"
                    }`}>
                      {removed
                        ? <X className="w-2.5 h-2.5 text-[#A8ABB2]" />
                        : <Check className="w-2.5 h-2.5 text-[#0A0A0A]" />
                      }
                    </span>
                    <span className={`transition-all duration-300 ${removed ? "line-through" : ""}`}>
                      {ing.name}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Modifiers - Glow Card Selection */}
        {product.modifiers?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-5 lux-surface p-6"
          >
            <h3 className="font-playfair text-xl text-[#F5F1E8] mb-1">{t("Extras & Add-ons")}</h3>
            <p className="text-[#A8ABB2] text-xs italic mb-4">{t("Customize your order")}</p>
            <div className="space-y-3">
              {product.modifiers.map((mod, i) => {
                const selected = selectedModifiers.find(m => m.modifierId === mod.id);
                return (
                  <motion.div
                    key={mod.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.45 + i * 0.06 }}
                    data-testid={`modifier-${mod.id}`}
                  >
                    <div
                      onClick={() => toggleModifier(mod)}
                      className={`p-4 rounded-xl border cursor-pointer transition-all duration-300 ${
                        selected
                          ? "border-[#D4B483] bg-[#D4B483]/8 modifier-glow"
                          : "border-[#27272A] bg-[#0A0A0A] hover:border-[#3F3F46] hover:bg-[#121212]"
                      }`}
                      data-testid={`modifier-toggle-${mod.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                            selected ? "border-[#D4B483] bg-[#D4B483] scale-110" : "border-[#3F3F46]"
                          }`}>
                            <AnimatePresence>
                              {selected && (
                                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
                                  <Check className="w-3 h-3 text-[#0A0A0A]" />
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                          <span className={`text-sm font-medium transition-colors ${selected ? "text-[#EDEDED]" : "text-[#D4D4D8]"}`}>
                            {mod.name}
                          </span>
                        </div>
                        <span className={`font-mono-price text-sm font-bold transition-colors ${selected ? "text-[#D4B483] price-glow" : "text-[#A8ABB2]"}`}>
                          +{mod.price.toFixed(0)} kr
                        </span>
                      </div>

                      {/* Quantity Controls */}
                      <AnimatePresence>
                        {selected && mod.max_quantity > 1 && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="mt-3 ml-8 flex items-center gap-3">
                              <button
                                className="w-8 h-8 rounded-full bg-[#1A1A1A] border border-[#27272A] hover:border-[#3F3F46] flex items-center justify-center text-[#EDEDED] transition-all active:scale-90"
                                onClick={() => updateModifierQty(mod.id, -1)}
                                data-testid={`mod-qty-minus-${mod.id}`}
                              >
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                              <motion.span
                                key={selected.quantity}
                                initial={{ scale: 1.3, opacity: 0.5 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="font-mono-price text-[#EDEDED] font-bold text-lg w-6 text-center"
                              >
                                {selected.quantity}
                              </motion.span>
                              <button
                                className="w-8 h-8 rounded-full bg-[#D4B483] hover:bg-[#E5CFA5] flex items-center justify-center text-[#0A0A0A] transition-all active:scale-90"
                                onClick={() => updateModifierQty(mod.id, 1)}
                                data-testid={`mod-qty-plus-${mod.id}`}
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                              <span className="text-[10px] text-[#A8ABB2] font-medium">max {mod.max_quantity}</span>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Floating Add to Cart Bar — portaled to <body> so no ancestor
          filter/transform can turn `fixed` into `absolute`. */}
      {createPortal(
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="fixed bottom-[calc(1rem_+_env(safe-area-inset-bottom,0px))] left-0 right-0 z-30 px-4"
        data-testid="add-to-cart-bar"
      >
        <div className="max-w-lg mx-auto glass rounded-2xl p-4 flex items-center gap-4 border border-[#D4B483]/20 shadow-[0_18px_50px_rgba(0,0,0,0.55)]">
          {/* Quantity */}
          <div className="flex items-center gap-2">
            <button
              className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-[#EDEDED] hover:border-[#3F3F46] transition-all active:scale-90"
              onClick={() => setQuantity(q => Math.max(1, q - 1))}
              data-testid="qty-minus"
            >
              <Minus className="w-4 h-4" />
            </button>
            <motion.span
              key={quantity}
              initial={{ scale: 1.3 }}
              animate={{ scale: 1 }}
              className="text-lg font-bold text-[#EDEDED] w-8 text-center font-mono-price"
            >
              {quantity}
            </motion.span>
            <button
              className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-[#EDEDED] hover:border-[#3F3F46] transition-all active:scale-90"
              onClick={() => setQuantity(q => q + 1)}
              data-testid="qty-plus"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Add Button with Glow */}
          <Button
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className="flex-1 bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-bold h-12 text-base btn-glow rounded-xl"
            data-testid="add-to-cart-btn"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            {isOutOfStock ? t("Out of stock") : `${t("Add")} · ${estimatedPrice().toFixed(0)} kr`}
          </Button>
        </div>
      </motion.div>,
      document.body
      )}
    </div>
  );
}
